import { Diagnostic } from "../models/diagnostic.js";
import { getPackageKnowledge } from "../knowledge/knowledgeBase.js";

/**
 * Checks project dependencies for common React Native libraries that require manual native integration.
 * Package-specific checks are delegated to the Plugin System in v0.9.
 */
export function checkDependencyDiagnostic(
  dependencies: Record<string, string>,
  devDependencies: Record<string, string>
): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];

  const hasDep = (name: string) => dependencies[name] !== undefined || devDependencies[name] !== undefined;
  const hasFirebase = hasDep("@react-native-firebase/app") ||
    Object.keys(dependencies).some((k) => k.startsWith("@react-native-firebase/")) ||
    Object.keys(devDependencies).some((k) => k.startsWith("@react-native-firebase/"));

  const packagesToCheck = [
    { name: "react-native-maps", id: "react-native-maps-info", title: "react-native-maps configuration reminder", condition: hasDep("react-native-maps") },
    { name: "@react-native-firebase/app", id: "react-native-firebase-info", title: "react-native-firebase setup reminder", condition: hasFirebase },
    { name: "react-native-reanimated", id: "react-native-reanimated-info", title: "react-native-reanimated babel config reminder", condition: hasDep("react-native-reanimated") },
    { name: "react-native-vision-camera", id: "react-native-vision-camera-info", title: "react-native-vision-camera permissions reminder", condition: hasDep("react-native-vision-camera") },
    { name: "react-native-mmkv", id: "react-native-mmkv-info", title: "react-native-mmkv configuration reminder", condition: hasDep("react-native-mmkv") },
    { name: "react-native-webview", id: "react-native-webview-info", title: "react-native-webview configuration reminder", condition: hasDep("react-native-webview") }
  ];

  for (const pkg of packagesToCheck) {
    if (pkg.condition) {
      const knowledge = getPackageKnowledge(pkg.name);
      if (knowledge) {
        let detected = `Found '${pkg.name}' in project dependencies.`;
        let explanation = "";
        let fixes: string[] = [];

        if (pkg.name === "react-native-maps") {
          explanation = "The 'react-native-maps' package requires additional native configuration for API keys and setup depending on the target map platform (Apple Maps vs Google Maps).";
          fixes = [
            "Android: Add your Google Maps API key to your 'android/app/src/main/AndroidManifest.xml' under <meta-data> inside <application>.",
            "iOS: If using Google Maps, install GoogleMaps Pods and call '[GMSServices provideAPIKey:...]' in AppDelegate.m/AppDelegate.mm."
          ];
        } else if (pkg.name === "@react-native-firebase/app") {
          detected = "Found '@react-native-firebase' in project dependencies.";
          explanation = "Firebase integration in React Native requires downloading native configuration files (google-services.json / GoogleService-Info.plist) and hooking up native build steps.";
          fixes = [
            "Android: Download 'google-services.json' from Firebase Console and place it in 'android/app/'. Add the google-services classpath and plugin to build.gradle files.",
            "iOS: Download 'GoogleService-Info.plist' from Firebase Console and add it to your Xcode project root. Initialize Firebase in AppDelegate."
          ];
        } else if (pkg.name === "react-native-reanimated") {
          explanation = "React Native Reanimated uses a custom Babel plugin to compile worklets. This plugin must be added to your Babel or Metro configuration.";
          fixes = [
            "Add 'react-native-reanimated/plugin' as the LAST item in your plugins array in your babel.config.js or metro.config.js file.",
            "Clear Metro bundler cache by running 'npm start -- --reset-cache' (or yarn/pnpm equivalent) after updating configurations."
          ];
        } else if (pkg.name === "react-native-vision-camera") {
          explanation = "Vision Camera requires camera and/or microphone usage description keys to be defined in your app's native metadata manifest files, otherwise Apple App Store submissions will be rejected and Android apps will crash at runtime.";
          fixes = [
            "iOS: Add NSCameraUsageDescription and/or NSMicrophoneUsageDescription keys and descriptive strings to 'ios/<Project>/Info.plist'.",
            "Android: Add camera permissions <uses-permission android:name=\"android.permission.CAMERA\" /> to 'android/app/src/main/AndroidManifest.xml'."
          ];
        } else {
          explanation = `The '${pkg.name}' package requires additional native configuration.`;
          fixes = knowledge.installHints;
        }

        diagnostics.push({
          id: pkg.id,
          title: pkg.title,
          severity: "info",
          detected,
          explanation,
          symptoms: knowledge.commonIssues || [],
          fixes,
          documentationUrl: knowledge.documentationUrl,
        });
      }
    }
  }

  return diagnostics;
}
